/**
 * The one place the console asks cp-auth-api to carry the refresh token in an HttpOnly cookie
 * (decision D21, ruling R-14c's two-homes precedent).
 *
 * The constant pair is the shared thing and it lives in `@zion/sdk-contracts`; this helper is the
 * console's own, as `@zion/sdk-react`'s `token-manager-utils.ts` is the DP shell's. Nothing else
 * in this package spells the header name.
 *
 * ⛔ NO `credentials` OPTION ANYWHERE (default F7). The console and cp-auth-api share one origin
 * per host, so `fetch`'s default `same-origin` credentials mode already attaches
 * `cp_refresh_token` under its `Path=/v1/auth`. Writing `credentials: 'include'` would add a CORS
 * requirement the deployment does not have and buy nothing. A cross-origin API base is not a
 * supported mode for cookie transport: the browser would attach no cookie and the refresh would
 * fall to the body arm. `setApiOriginAllowlist` in `runtime-config.ts` has no production caller
 * and the allowlist is empty by default, so the console and cp-auth-api share one origin per
 * host.
 */
import { SESSION_TRANSPORT_COOKIE, SESSION_TRANSPORT_HEADER } from '@zion/sdk-contracts';

/**
 * The request header that selects cookie transport, ready to spread into a `fetch` header map.
 *
 * Callers: the six session-answering calls in `auth.api.ts` — `login`, `passkeyLogin`,
 * `oauthExchange`, `changePassword`, `refreshTokens` and `logout` (R-50). A route that mints or
 * ends a session and does not carry this header gets today's body-arm behaviour, which during the
 * migration means a refresh token in the JSON body and no cookie.
 *
 * @returns a fresh object, always exactly one entry:
 *   `{ 'x-zion-session-transport': 'cookie' }`. A fresh object per call, so a caller that spreads
 *   it and then mutates its own header map cannot reach back into a shared one.
 * @throws nothing. It reads no storage, no DOM and no network.
 */
export function sessionTransportHeaders(): Record<string, string> {
  return { [SESSION_TRANSPORT_HEADER]: SESSION_TRANSPORT_COOKIE };
}
